import { clsx } from 'clsx';
import { Slider as SliderPrimitive } from 'radix-ui';

export interface SliderProps {
  value: readonly number[];
  onValueChange?: ((value: number[]) => void) | undefined;
  /** Fires when the pointer is released / a key press settles — use it to commit to the URL. */
  onValueCommit?: ((value: number[]) => void) | undefined;
  min: number;
  max: number;
  step?: number | undefined;
  /** One label per thumb (`['Minimum price', 'Maximum price']`) — required for screen readers. */
  thumbLabels: readonly string[];
  /** Spoken value per thumb ("₹1,200") — otherwise screen readers announce the raw number. */
  valueText?: ((value: number, index: number) => string) | undefined;
  minStepsBetweenThumbs?: number | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
}

/** Single- or dual-thumb slider on Radix with a brass range and 24 px thumbs (44 px hit area). */
export function Slider({
  value,
  onValueChange,
  onValueCommit,
  min,
  max,
  step = 1,
  thumbLabels,
  valueText,
  minStepsBetweenThumbs,
  disabled,
  className,
}: SliderProps) {
  return (
    <SliderPrimitive.Root
      className={clsx('slider', className)}
      value={[...value]}
      min={min}
      max={max}
      step={step}
      {...(onValueChange ? { onValueChange } : {})}
      {...(onValueCommit ? { onValueCommit } : {})}
      {...(minStepsBetweenThumbs !== undefined ? { minStepsBetweenThumbs } : {})}
      {...(disabled ? { disabled } : {})}
    >
      <SliderPrimitive.Track className="slider__track">
        <SliderPrimitive.Range className="slider__range" />
      </SliderPrimitive.Track>
      {value.map((current, index) => (
        <SliderPrimitive.Thumb
          key={index}
          className="slider__thumb"
          aria-label={thumbLabels[index] ?? ''}
          {...(valueText ? { 'aria-valuetext': valueText(current, index) } : {})}
        />
      ))}
    </SliderPrimitive.Root>
  );
}
